import { useCallback, useState, useEffect, useMemo } from 'react';
// tslint:disable-next-line:import-blacklist
import { useLocation } from 'react-router-dom';
import { useHistory } from '@shared/routing';
function useIsRouterAvailable() {
    try {
        useLocation();
        return true;
    }
    catch (_a) {
        return false;
    }
}
function useCurrentLocation() {
    const [search, setSearch] = useState(() => window.location.search);
    const isRouterAvailable = useIsRouterAvailable();
    useEffect(() => {
        const handleUrlChange = () => {
            setSearch(window.location.search);
        };
        window.addEventListener('popstate', handleUrlChange);
        return () => {
            window.removeEventListener('popstate', handleUrlChange);
        };
    }, []);
    if (isRouterAvailable) {
        const location = useLocation();
        return {
            search: location.search,
            pathname: location.pathname,
            hash: location.hash,
            isRouter: true
        };
    }
    return {
        search,
        pathname: window.location.pathname,
        hash: window.location.hash,
        isRouter: false
    };
}
/**
 * Хук для работы с query параметрами URL (аналог useSearchParams из React Router v6)
 * @returns {[URLSearchParams, Function]} Массив из объекта URLSearchParams и функции для обновления параметров
 */
export function useSearchParams() {
    const location = useCurrentLocation();
    const history = location.isRouter ? useHistory() : null;
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const setSearchParams = useCallback((nextInit, navigateOpts = {}) => {
        let newSearchParams;
        if (typeof nextInit === 'function') {
            const currentParams = new URLSearchParams(location.search);
            newSearchParams = nextInit(currentParams);
        }
        else if (nextInit instanceof URLSearchParams) {
            newSearchParams = nextInit;
        }
        else if (typeof nextInit === 'object') {
            newSearchParams = new URLSearchParams();
            Object.entries(nextInit).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    newSearchParams.set(key, String(value));
                }
            });
        }
        const newSearch = newSearchParams.toString();
        const { replace = false, state } = navigateOpts;
        if (history) {
            const newPath = {
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : '',
                hash: location.hash,
                state
            };
            if (replace) {
                history.replace(newPath);
            }
            else {
                history.push(newPath);
            }
        }
        else {
            const url = new URL(window.location.href);
            url.search = newSearch ? `?${newSearch}` : '';
            if (replace) {
                window.history.replaceState(state || null, '', url.toString());
            }
            else {
                window.history.pushState(state || null, '', url.toString());
            }
        }
    }, [history, location.pathname, location.search, location.hash]);
    return [searchParams, setSearchParams];
}
